import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Clock, BookOpen, Zap, Tag, Loader2 } from 'lucide-react';
import { fetchPosts } from '../lib/supabase';
import './Blog.css';

const CAT_COLORS = {
  Guide: '#6d4fff', Analysis: '#00e5ff', Playbook: '#22d3a0',
  Roundup: '#f472b6', Comparison: '#fbbf24',
};

export default function Blog() {
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPosts()
      .then(setAllPosts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...Array.from(new Set(allPosts.map(p => p.category))).sort()];

  const filtered = allPosts.filter(p => {
    const matchCat = filter === 'All' || p.category === filter;
    const matchQ = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchQ;
  });

  const featured = filtered.find(p => p.featured);
  const rest = filtered.filter(p => !p.featured || filter !== 'All' || search);

  return (
    <main style={{ paddingTop: 'var(--nav-h)' }}>
      <section className="section">
        <div className="section-inner">
          <div className="section-label"><BookOpen size={12} /> ToolMind Blog</div>
          <h1 className="section-title">Insights for <span className="grad-text">tool-smart builders</span></h1>
          <p className="section-sub">Guides, comparisons, and playbooks on building better with the right tools.</p>

          <div className="blog__search glass-hi">
            <Search size={16} className="blog__search-icon" />
            <input
              className="blog__search-input"
              placeholder="Search articles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="blog__cats">
            {categories.map(c => (
              <button
                key={c}
                className={`tools-page__cat ${filter === c ? 'tools-page__cat--active' : ''}`}
                onClick={() => setFilter(c)}
              >{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '0 var(--sp-6) var(--sp-20)' }}>
        <div className="section-inner">
          {loading ? (
            <div className="tools-page__empty glass">
              <Loader2 size={28} className="tools-page__spinner" style={{ color: 'var(--text-accent)' }} />
              <p style={{ color: 'var(--text-muted)' }}>Loading articles…</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && filter === 'All' && !search && (
                <Link to={`/blog/${featured.slug}`} className="blog__featured glass-hi" style={{ display: 'grid' }}>
                  <div className="blog__featured-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
                      <span className="blog-cat-tag" style={{ '--cat-c': CAT_COLORS[featured.category] || '#6d4fff' }}>{featured.category}</span>
                      <span className="blog-meta"><Clock size={12} />{featured.read_time} read</span>
                    </div>
                    <h2 className="blog__featured-title">{featured.title}</h2>
                    <p className="blog__featured-excerpt">{featured.excerpt}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)', marginTop: 'var(--sp-8)' }}>
                      <div className="blog__author">
                        <div className="blog__avatar">{featured.author_avatar}</div>
                        <span>{featured.author_name}</span>
                      </div>
                      <span className="btn btn-primary btn-sm">
                        Read article <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                  <div className="blog__featured-visual">
                    <div className="blog__featured-glow" />
                    <Zap size={64} style={{ color: 'rgba(109,79,255,0.3)', position: 'relative', zIndex: 1 }} />
                  </div>
                </Link>
              )}

              {/* Grid */}
              <div className="blog__grid">
                {(filter !== 'All' || search ? filtered : rest).map(post => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="blog-card glass">
                    <div className="blog-card__top">
                      <span className="blog-cat-tag" style={{ '--cat-c': CAT_COLORS[post.category] || '#6d4fff' }}>{post.category}</span>
                      <span className="blog-meta"><Clock size={12} />{post.read_time}</span>
                    </div>
                    <h3 className="blog-card__title">{post.title}</h3>
                    <p className="blog-card__excerpt">{post.excerpt}</p>
                    <div className="blog-card__tags">
                      {post.tags?.map(t => (
                        <span key={t} className="tag"><Tag size={10} />{t}</span>
                      ))}
                    </div>
                    <div className="blog-card__footer">
                      <div className="blog__author">
                        <div className="blog__avatar">{post.author_avatar}</div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{post.author_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                      <span className="btn btn-ghost btn-sm">Read <ArrowRight size={13} /></span>
                    </div>
                  </Link>
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="tools-page__empty glass">
                  <Search size={32} style={{ color: 'var(--text-muted)' }} />
                  <p style={{ color: 'var(--text-muted)' }}>No posts match your search.</p>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilter('All'); }}>Clear</button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}
